import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubTabs, useAdminGet } from '../../kit';
import EditableTemplates from '../EditableTemplates';
import { TemplateLibrary } from '../library/TemplateLibrary';
import { LIBRARY_KEY_PREFIX, MEMAC_GROUPS, MEMAC_KEY_PREFIX, SECTION, normOverview, normTriggers } from './shared';
import OverviewTab from './Overview';
import Triggers from './Triggers';
import TriggerEditor from './TriggerEditor';
import Runs from './Runs';
import SettingsTab from './Settings';
import { TemplateTimings, normSchedules } from './Schedule';
import { CohortPreview, PreviewAll } from './CohortPreview';
import Stats from './Stats';

/**
 * Email Center → MEMAC (Member Engagement Matrix & Cadence, spec of
 * 28 Sep 2026; owner, 2026-10-11): the statuses × cohorts, every trigger in
 * the spec with its template, and the switches — all Off until an admin
 * turns one on here (backend: Backend/memac.py, section "email", memac_*).
 *
 *   Overview      status × cohort counts, cohort definitions, the global
 *                 switch state and a "nothing is sent automatically" banner
 *   Triggers      the spec's tables; a row opens its editor (signal, audience,
 *                 content, caps), Dry run, Send a test to me, Send to these N
 *                 now (typed count), Reset / Delete; New trigger
 *   Templates     two segments: the MEMAC built-in e-mails (Templates
 *                 (editable) narrowed to the MEMAC groups) and the Saved
 *                 templates shared with Campaigns (new, duplicate, archive,
 *                 versions — the same library as Email Center → Saved templates)
 *   Stats         each trigger's results, campaign style: sent, delivered,
 *                 opened, clicked, CTA clicks, unsubscribes, and its recipients
 *   Runs & log    every dry run / manual / automatic run, and a trigger's sends
 *   Settings      global switch (with confirm), window, interval, caps
 *
 * Across the tabs (owner, 2026-10-25): each trigger's real timing ("When") and
 * a countdown to its next automatic send — live when every gate is open, "if
 * enabled" otherwise (email/memac_schedule) — and Preview by cohort / Preview
 * all: the mail each status × cohort segment would get now
 * (email/memac_cohort_preview; reads only, never recorded).
 *
 * Deep link (optional props, from EmailPanel): `trigger` opens that
 * trigger's editor, `view` picks a sub-tab, `template` opens Templates on
 * that template (a 'lib_…' one under Saved templates); `nonce` re-applies them.
 */

const VIEWS = ['Overview', 'Triggers', 'Stats', 'Templates', 'Runs & log', 'Settings'];
const VIEW_BY_KEY: Record<string, string> = {
  overview: 'Overview', matrix: 'Overview', triggers: 'Triggers', stats: 'Stats', results: 'Stats', templates: 'Templates',
  saved_templates: 'Templates', library: 'Templates',
  runs: 'Runs & log', log: 'Runs & log', settings: 'Settings',
};

/** The Templates sub-tab's two halves. */
type TplSegment = 'builtin' | 'library';
const TPL_SEGMENTS: [TplSegment, string][] = [
  ['builtin', 'MEMAC built-in e-mails'],
  ['library', 'Saved templates (shared with Campaigns)'],
];
const segmentOf = (key: string): TplSegment => (key.startsWith(LIBRARY_KEY_PREFIX) ? 'library' : 'builtin');

export type MemacPanelProps = {
  trigger?: string | null; view?: string | null; template?: string | null; nonce?: string | null;
};

const MemacPanel: React.FC<MemacPanelProps> = ({ trigger, view: viewIn, template, nonce }) => {
  const viewKey = String(viewIn || '').toLowerCase();
  const [view, setView] = useState(() => VIEW_BY_KEY[viewKey]
    || (trigger ? 'Triggers' : template ? 'Templates' : VIEWS[0]));
  // null = closed; '' = a new trigger; otherwise the trigger id.
  const [editing, setEditing] = useState<string | null>(trigger || null);
  const [editorSeq, setEditorSeq] = useState(0);
  const [runsFor, setRunsFor] = useState<string | undefined>(undefined);
  // Templates: which half, and the template to open there (re-applied on each open).
  const [tplSeg, setTplSeg] = useState<TplSegment>(() =>
    template ? segmentOf(template) : viewKey === 'saved_templates' || viewKey === 'library' ? 'library' : 'builtin');
  const [tplKey, setTplKey] = useState(template || '');
  const [tplSeq, setTplSeq] = useState(0);
  const openTemplates = (key?: string) => {
    setEditing(null);
    setView('Templates');
    if (key) setTplSeg(segmentOf(key));
    setTplKey(key || '');
    setTplSeq(n => n + 1);
  };

  useEffect(() => {
    if (!trigger && !viewIn && !template) return;
    const v = VIEW_BY_KEY[viewKey];
    if (v) setView(v);
    if (viewKey === 'saved_templates' || viewKey === 'library') setTplSeg('library');
    if (template) openTemplates(template);
    if (trigger) { setView(v || 'Triggers'); setEditing(trigger); setEditorSeq(n => n + 1); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, trigger, viewIn, template]);

  const ovRes = useAdminGet(SECTION, 'memac_overview', {}, []);
  const trRes = useAdminGet(SECTION, 'memac_triggers', {}, []);
  const ov = useMemo(() => (ovRes.data ? normOverview(ovRes.data) : null), [ovRes.data]);
  const { triggers, razorpay, templates } = useMemo(() => normTriggers(trRes.data), [trRes.data]);
  // Settings' global switch-on lists the On + Auto triggers: only from a read that succeeded.
  const triggersReady = !trRes.loading && !trRes.error && trRes.data !== null && trRes.data !== undefined;
  // When each trigger next sends on its own (the countdowns): read after the
  // list, refreshed with it, every 5 minutes, and soon after a countdown ends.
  const [schedSeq, setSchedSeq] = useState(0);
  const schRes = useAdminGet(SECTION, 'memac_schedule', {}, [schedSeq]);
  // The clock offset is taken when an answer arrives, never re-taken on a reload.
  const schBase = useMemo(() => normSchedules(schRes.data, false, ''), [schRes.data]);
  const sch = useMemo(() => ({ ...schBase, loading: schRes.loading, error: schRes.error }),
    [schBase, schRes.loading, schRes.error]);
  const reloadSchedule = useCallback(() => setSchedSeq(n => n + 1), []);
  useEffect(() => {
    const id = window.setInterval(reloadSchedule, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [reloadSchedule]);
  const elapsedAt = useRef(0);
  const onElapsed = useCallback(() => {
    // A countdown reached zero: ask again shortly (once per half minute at most).
    if (Date.now() - elapsedAt.current < 30000) return;
    elapsedAt.current = Date.now();
    window.setTimeout(reloadSchedule, 4000);
  }, [reloadSchedule]);
  // The last 30 days' results per trigger, for the Triggers list (email/stats_triggers; reads only).
  const [statsSeq, setStatsSeq] = useState(0);
  const st30Res = useAdminGet(SECTION, 'stats_triggers', { days: 30 }, [statsSeq]);
  const stats30 = useMemo(() => Object.fromEntries(
    (Array.isArray((st30Res.data as any)?.triggers) ? (st30Res.data as any).triggers : [])
      .map((r: any) => [String(r.trigger_id), r])), [st30Res.data]);
  const reloadAll = () => { ovRes.reload(); trRes.reload(); reloadSchedule(); setStatsSeq(n => n + 1); };
  // Preview by cohort (one trigger) and Preview all.
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [previewAll, setPreviewAll] = useState(false);

  const open = (id: string) => { setEditing(id); setEditorSeq(s => s + 1); };
  const openRuns = (id: string) => { setRunsFor(id); setEditing(null); setView('Runs & log'); };

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={VIEWS} active={view} onChange={v => { setView(v); if (v !== 'Runs & log') setRunsFor(undefined); }} />
      {view === 'Overview' && (
        <OverviewTab ov={ov} loading={ovRes.loading} error={ovRes.error} triggers={triggers}
          onReload={reloadAll} onOpenTrigger={open} onOpenSettings={() => setView('Settings')} />
      )}
      {view === 'Triggers' && (
        <Triggers ov={ov} triggers={triggers} razorpay={razorpay} loading={trRes.loading} error={trRes.error}
          onReload={reloadAll} onOpen={open} sch={sch} onElapsed={onElapsed}
          onPreview={setPreviewFor} onPreviewAll={() => setPreviewAll(true)}
          stats30={stats30} onOpenStats={() => setView('Stats')} />
      )}
      {view === 'Stats' && <Stats triggers={triggers} onOpen={open} stamp={statsSeq} />}
      {view === 'Templates' && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-1 flex-wrap">
            {TPL_SEGMENTS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => { setTplSeg(k); setTplKey(''); }}
                className={`py-1 px-3 rounded-md text-xs font-grotesk ${tplSeg === k
                  ? 'bg-primary-100 text-grayscale-1300 font-bold border border-primary-300'
                  : 'border border-grayscale-300 text-grayscale-1200 hover:bg-grayscale-200'}`}>
                {label}
              </button>
            ))}
          </div>
          {tplSeg === 'builtin' ? (
            <>
              <p className="text-xs text-grayscale-1100">
                The MEMAC e-mails. An edit takes effect on the next send; Reset brings the built-in copy back. The live
                mails MEMAC lists but does not send (welcome, trial, payment) stay in Email Center → Templates (editable).
                To make a new one, duplicate a built-in under Saved templates (Start from… → Built-in e-mails).
              </p>
              <TemplateTimings triggers={triggers} sch={sch} onOpenTrigger={open} onElapsed={onElapsed} />
              <EditableTemplates key={`b${tplSeq}`} groups={MEMAC_GROUPS} keyPrefix={MEMAC_KEY_PREFIX} title="MEMAC templates"
                initialKey={tplKey && !tplKey.startsWith(LIBRARY_KEY_PREFIX) ? tplKey : undefined} />
            </>
          ) : (
            // The same list as Email Center → Saved templates (it explains itself).
            <TemplateLibrary key={`l${tplSeq}`} origin="memac" compact
              initialKey={tplKey.startsWith(LIBRARY_KEY_PREFIX) ? tplKey : undefined} />
          )}
        </div>
      )}
      {view === 'Runs & log' && <Runs triggers={triggers} initial={runsFor} onOpenTrigger={open} />}
      {view === 'Settings' && (
        <SettingsTab ov={ov} loading={ovRes.loading} error={ovRes.error} triggers={triggers}
          triggersReady={triggersReady} triggersError={trRes.error}
          onChanged={reloadAll} onOpenTrigger={open} />
      )}
      {editing !== null && (
        // Keyed on the open, not the id: a new trigger keeps its editor (and its
        // "Created" note) when it turns into the saved one.
        <TriggerEditor key={editorSeq} id={editing} ov={ov} triggers={triggers}
          templates={templates} templatesError={trRes.error}
          onClose={() => setEditing(null)}
          onChanged={openId => { reloadAll(); if (openId && openId !== editing) setEditing(openId); }}
          onOpenTemplates={openTemplates}
          onOpenRuns={openRuns} onOpenTrigger={open}
          sch={sch} onElapsed={onElapsed} onPreviewCohorts={setPreviewFor} />
      )}
      {previewFor && (
        <CohortPreview triggerId={previewFor} triggers={triggers} sch={sch} onElapsed={onElapsed}
          onClose={() => setPreviewFor(null)} />
      )}
      {previewAll && (
        <PreviewAll triggers={triggers} sch={sch} onElapsed={onElapsed} onClose={() => setPreviewAll(false)}
          onOpenTrigger={id => { setPreviewAll(false); open(id); }} />
      )}
    </div>
  );
};

export default MemacPanel;
